import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { noop, get, filter, toLower, isEqual, isEmpty } from 'lodash';
import { Trans, withI18n } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';
import styled, { css } from 'styled-components';

import { Row, Container, Column } from 'styled-components-reactive-grid';
import { Text, Dropdown as DefaultDropdown, Tooltip } from 'base-components';
import { buildStyledComponent, px2rem } from 'decisiv-ui-utils';

import { useFocusReceiver } from 'setup/FocusProvider';

import HighlightText from 'components/HighlightText';

import InputField from './InputField';
import { seeNotesRequestValue } from '../constants';
import { fieldIds } from '../../constants';

/**
 * TODO: should these be default values of Dropdown.Content? They make the
 * dropdown's content grow in width what it needs to fit the longest option.
 * Before: https://seven.d.pr/jq3GGD
 * After: https://seven.d.pr/JHETSN
 */
export const dropdownContentStyles = {
  right: 'auto',
  whiteSpace: 'nowrap',
  maxHeight: px2rem(300),
  overflowY: 'auto',
  zIndex: 2,
};

const Dropdown = buildStyledComponent(
  'Dropdown',
  styled(DefaultDropdown),
  ({ isNonPreferredSelection, theme }) =>
    isNonPreferredSelection &&
    css`
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        border: ${px2rem(5)} solid transparent;
        border-right-color: ${theme.colors.status.info};
        border-top-color: ${theme.colors.status.info};
        z-index: 1;
        pointer-events: none;
      }
    `,
);

// TODO: should these be added to Dropdown.List and Input.ActionButton by default?
// This enables visual feedback for navigating with tab key through the list
const DropdownList = buildStyledComponent(
  'Dropdown__List',
  styled(Dropdown.List),
  ({ theme }) =>
    css`
      li button:focus {
        background-color: ${theme.colors.base.selectableHover};
        span {
          color: ${theme.colors.base.linkHover};
          text-decoration: underline;
        }
      }
    `,
);
const InputFieldActionButton = buildStyledComponent(
  'InputField__ActionButton',
  styled(InputField.ActionButton),
  ({ theme }) =>
    css`
      &:focus {
        color: ${theme.colors.status.info};
      }
    `,
);

function getSelectedOption(props) {
  const { value, options, i18n, rowData = {} } = props;

  const dependsOn = props.dependsOn || [];
  const lowerCasedValue = toLower(value);

  if (value === seeNotesRequestValue) {
    return { id: value, title: i18n._('See Notes') };
  }

  const option = options.find(
    ({ id, title }) => value === id || lowerCasedValue === toLower(title),
  );

  if (!option && dependsOn.some(key => rowData[key] === seeNotesRequestValue)) {
    return { id: value, title: value };
  }

  return option;
}

export class DropdownCell extends Component {
  static propTypes = {
    i18n: PropTypes.shape({ t: PropTypes.func.isRequired }).isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    readOnly: PropTypes.bool,
    onChange: PropTypes.func,
    autoSelectSingleOption: PropTypes.bool,
    isNonPreferredSelection: PropTypes.bool,
    optional: PropTypes.bool,
    autocomplete: PropTypes.bool,
    onReset: PropTypes.func,
    disabledOptionIds: PropTypes.arrayOf(PropTypes.string),
    rowData: PropTypes.shape({}),
    dependsOn: PropTypes.arrayOf(PropTypes.string),
    onFocusRequested: PropTypes.func.isRequired,
  };

  static defaultProps = {
    value: undefined,
    readOnly: false,
    onChange: noop,
    autoSelectSingleOption: false,
    isNonPreferredSelection: false,
    optional: false,
    onReset: noop,
    disabledOptionIds: [],
    autocomplete: false,
    rowData: {},
    dependsOn: [],
  };

  static getDerivedStateFromProps(props, state) {
    let statePatch = {};

    const { autocomplete, options, value } = props;
    const {
      prevInputValue,
      inputValue,
      visibleOptions,
      selectedValue,
      prevOptions,
    } = state;

    const lowerCasedInputValue = toLower(inputValue);

    if (inputValue !== prevInputValue || options !== prevOptions) {
      // Filter options by typed input value when autocomplete is active
      const newVisibleOptions = autocomplete
        ? filter(options, o => toLower(o.title).includes(lowerCasedInputValue))
        : options;

      if (!isEqual(visibleOptions, newVisibleOptions)) {
        statePatch.visibleOptions = newVisibleOptions;
      }
    }

    const inputValuesHaveChanged = [
      value !== selectedValue,
      options !== prevOptions,
    ].some(Boolean);

    if (inputValuesHaveChanged) {
      const selectedOption = getSelectedOption(props);

      statePatch = {
        ...statePatch,
        inputValue: get(selectedOption, 'title') || '',
        prevInputValue: inputValue,
        selectedOption,
        selectedValue: value,
        prevOptions: options,
      };
    }

    return !isEmpty(statePatch) ? statePatch : null;
  }

  constructor(props) {
    super(props);

    const selectedOption = getSelectedOption(props);

    this.state = {
      // Set the initial value of input field
      inputValue: get(selectedOption, 'title') || '',
      selectedOption,
      visibleOptions: props.options,
      // the following are used in getDerivedStateFromProps
      /* eslint-disable react/no-unused-state */
      prevInputValue: '',
      prevOptions: props.options,
      selectedValue: props.value,
      /* eslint-enable react/no-unused-state */
    };
  }

  componentWillUnmount() {
    clearTimeout(this.onFocusShowTimeout);
    clearTimeout(this.blurTimeout);
  }

  onInputChange = e => {
    e.preventDefault();

    if (this.props.autocomplete) {
      this.updateInputValue(e.target.value);
    }
  };

  setInnerRef = name => node => {
    this[name] = node;
    return this[name];
  };

  updateInputValue = newValue =>
    this.setState(({ inputValue }) => ({
      inputValue: newValue,
      prevInputValue: inputValue,
    }));

  handleChange = (value, isAutoSelectingSingleOption = false) => {
    const { onChange, options } = this.props;

    clearTimeout(this.blurTimeout);

    if (typeof value === 'string') {
      let newOption = getSelectedOption({ value, options });
      const currentOption = this.state.selectedOption;

      // If new value doesn't match an option, reset to currently selected option.
      // Useful for when focus is out of input/dropdown leaving it incomplete.
      // Unless new value is empty, in which case we want to leave it empty.
      if (value && !newOption) {
        newOption = currentOption;
      }

      const newValue = get(newOption, 'title', '');
      const newId = get(newOption, 'id', '');

      this.updateInputValue(newValue);

      // Only trigger change upwards if selected option changed
      if (
        newId !== get(currentOption, 'id') &&
        (newId || get(currentOption, 'id'))
      ) {
        onChange(newId, isAutoSelectingSingleOption);
      }
    }
  };

  handleBlur = event => {
    // Only trigger onBlur change when dropdown is out of focus
    // to help prevent erasing selected option when navigating with tab key
    // and when selecting a new option from the dropdown

    // TODO: stop accessing dropdown's internal methods/state, which is dangerous
    const { innerRef, state, toggleContent } = this.dropdownRef || {};

    const isFocusOutside = !(
      get(innerRef, 'contains') && innerRef.contains(event.relatedTarget)
    );

    if (isFocusOutside) {
      this.handleChange(event.target.value);

      // TODO: should hiding when out-of-focus be handled on Dropdown itself by default just like click-outside is?
      get(state, 'showContent') &&
        toggleContent &&
        toggleContent({ show: false });
    }
  };

  renderInputField = ({ disabled, isVisible, show, toggle, ...rest }) => {
    const { i18n, readOnly, optional, onReset, autocomplete } = this.props;
    const { inputValue } = this.state;

    const onFocus = () => {
      // Delay showing the dropdown when receiving focus, to prevent
      // flashing when the focus event is the result of clicking the
      // chevron.
      this.onFocusShowTimeout = setTimeout(show, 100);
    };

    return (
      <InputField
        value={inputValue}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={i18n.t`Select...`}
        onChange={this.onInputChange}
        onFocus={onFocus}
        {...rest}
      >
        <Column modifiers={['col', 'padScaleY_0']}>
          <Row>
            <InputField.TextField
              readOnly={readOnly || !autocomplete}
              innerRef={node => {
                this.setInnerRef('inputRef')(node);
                node && this.props.onFocusRequested(node);
              }}
            />
            {(optional || autocomplete) &&
              inputValue && (
                <InputField.ActionButton
                  icon="times"
                  type="button"
                  onClick={e => {
                    e.stopPropagation();

                    autocomplete ? this.handleChange('') : onReset();
                    this.inputRef && this.inputRef.focus();
                  }}
                  modifiers={['padScaleX_0', 'hoverDanger']}
                />
              )}
            <InputFieldActionButton
              icon={isVisible ? 'chevron-up' : 'chevron-down'}
              onClick={e => {
                e.stopPropagation();
                toggle();
              }}
              modifiers={['hoverInfo']}
            />
          </Row>
        </Column>
      </InputField>
    );
  };

  renderOptionsList = (options, selectedId, highlightText) => {
    const { disabledOptionIds, autocomplete } = this.props;

    const renderTrans = ({ translation }) => {
      if (!autocomplete) return translation;

      return <HighlightText text={highlightText}>{translation}</HighlightText>;
    };

    return (
      <Dropdown.Content style={dropdownContentStyles}>
        <DropdownList>
          {options.map(({ id, title }) => (
            <Dropdown.ListItem
              id={id}
              key={`${id}.${title}`}
              modifiers={
                id !== selectedId && disabledOptionIds.includes(id)
                  ? ['disabled']
                  : undefined
              }
            >
              <Text>
                <Trans id={title || ''} render={renderTrans} />
              </Text>
            </Dropdown.ListItem>
          ))}
        </DropdownList>
      </Dropdown.Content>
    );
  };

  render() {
    const {
      i18n,
      name,
      value,
      options,
      onChange,
      readOnly,
      autoSelectSingleOption,
      isNonPreferredSelection,
      optional,
      onReset,
      disabledOptionIds,
      autocomplete,
      ...rest
    } = this.props;

    const { inputValue, visibleOptions, selectedOption } = this.state;

    const selectedId = get(selectedOption, 'id');
    const selectedTitle = get(selectedOption, 'title');

    // When no value is selected and there
    // is only one item, automatically select it.
    if (autoSelectSingleOption && !value && options.length === 1) {
      this.handleChange(get(options, '0.id'), true);
    }

    const disabled = !options.length && !selectedId;

    return (
      <Dropdown
        name={name}
        innerRef={this.setInnerRef('dropdownRef')}
        onChange={(e, newValue) => this.handleChange(newValue)}
        readOnly={readOnly}
        onBlur={e => {
          e.persist();

          // Delay this to allow clicks inside the popover (selecting an option) to
          // register as a change. Without the delay, clicking an option when the
          // popover was opened due to a focus event, won't trigger a valid change.
          this.blurTimeout = setTimeout(() => this.handleBlur(e), 150);
        }}
        fullWidth
        activeItem={selectedId}
        hideOnChange
        isNonPreferredSelection={isNonPreferredSelection}
      >
        {({ isVisible, show, toggle }) => {
          const inputField = this.renderInputField({
            show,
            toggle,
            disabled,
            isVisible,
            ...rest,
          });

          let tooltipContent =
            disabled && !readOnly ? <Trans>No option available.</Trans> : null;

          if (!disabled && selectedTitle) {
            tooltipContent = <span>{selectedTitle}</span>;
          }

          return (
            <Fragment>
              <Container
                onClick={disabled ? undefined : show}
                modifiers="padScale_0"
              >
                <Tooltip>
                  <Tooltip.Target>{inputField}</Tooltip.Target>
                  {tooltipContent && (
                    <Tooltip.Content>{tooltipContent}</Tooltip.Content>
                  )}
                </Tooltip>
              </Container>
              {!!visibleOptions.length &&
                this.renderOptionsList(visibleOptions, selectedId, inputValue)}
            </Fragment>
          );
        }}
      </Dropdown>
    );
  }
}

const getFieldId = ({ rowType, name, rowData: { id } }) =>
  get(fieldIds, [rowType, name], '').replace('{id}', id);

export default compose(
  setDisplayName('DropdownCell'),
  withI18n(),
  useFocusReceiver(getFieldId),
)(DropdownCell);



// WEBPACK FOOTER //
// ./src/compositions/CaseRequestsPanel/CaseRequestLines/BaseRequestLinesTable/DropdownCell/index.js